import { useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router';
import EnvironmentVariablesContext
  from '../contexts/EnvironmentVariablesContext';
import makeAnalyticsConfiguration from '../utils/makeAnalyticsConfiguration';
import AnalyticsContext from '../contexts/AnalyticsContext';
import useAwaitQueries from './useAwaitQueries';


export const defaultConfiguration = {
  skip: true,
  scopedDimensions: true,
  awaitQueries: [],
  fields: () => ({}),
};

function set(fieldsObject) {
  let _fieldsObject = { ...fieldsObject };


  if (Object.keys(_fieldsObject).length === 0) {
    return;
  }

  window.gtag('set', 'user_properties', _fieldsObject);
}


function event(environmentVariables, args, fieldsObject = {}) {
  
  const configuration = makeAnalyticsConfiguration(environmentVariables);

  configuration.trackers.forEach(tracker => {
    if (tracker.trackingId === undefined) return;

    window.gtag('event', args.label, {
      category: args.category,
      action: args.action,
      label: args.label,
      value: args.value,
      ...fieldsObject,
      send_to: tracker.trackingId,
    });
  });

}

export default function useAnalytics({
  skip = defaultConfiguration.skip,
  scopedDimensions = defaultConfiguration.scopedDimensions,
  awaitQueries = defaultConfiguration.awaitQueries,
  fields = defaultConfiguration.fields,
} = defaultConfiguration) {
  const environmentVariables = useContext(EnvironmentVariablesContext);
  const [readyAwaitQueries, awaitQueriesReady] = useAwaitQueries(awaitQueries);
  const location = useLocation();
  const locationPath = location.pathname + location.search + location.hash;

  const {
    pageviewSentForLocation,
  } = useContext(AnalyticsContext);

  useEffect(function() {
    if (awaitQueriesReady === false) return;

    const fieldsObject = {
      // We make sure to always track the right location and page by setting
      // the `location` and `page` whenever we set dimensions or track a
      // pageview.
      location: window.location.href,
      page: locationPath,
      ...fields(readyAwaitQueries),
    };

    if (scopedDimensions === false) {
      set(fieldsObject);
    }

    if (
      skip === false &&
      pageviewSentForLocation.current !== locationPath
    ) {

      const configuration = makeAnalyticsConfiguration(environmentVariables);

      configuration.trackers.forEach(tracker => {
        if (tracker.trackingId === undefined) return;

        window.gtag('event', 'page_view', {
          ...fieldsObject,
          send_to: tracker.trackingId,
        });
      });

      // We keep track on which path we sent a "send" event to Google Analytics
      // to avoid sending the same page multiple times. We could create a
      // dedicated useEffect but we need to make sure ReactGA.pageview is called
      // after ReactGA.set.
      pageviewSentForLocation.current = locationPath;
    }
  }, [
    awaitQueries.length,
    awaitQueriesReady,
    environmentVariables,
    fields,
    locationPath,
    pageviewSentForLocation,
    readyAwaitQueries,
    scopedDimensions,
    skip,
  ]);

  const eventWithEnvironmentVariables = useCallback(function(args, fieldsObject) {
    event(environmentVariables, args, fieldsObject)
  }, [environmentVariables]);

  return {
    set,
    event: eventWithEnvironmentVariables,
  };
}
